import React, {useEffect, useRef, useState} from 'react';
import {FormPageProps, WdiFormPageTitle} from '@/components/WdiBus/FormPage';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {
  approvalConfig,
  initApprovalPageContext,
  refreshApprovalPageContext,
  moreConfig,
  ViewEntityProps,
} from '@/pages/contract/RebatePaymentApply/components/data';
import {wdiCore, WdiInput, WdiStaffCard} from '@/components/WdiControls';
import {ResponseStatus} from '@/utils/request';
import {Badge, Col, Form, message, Row, Spin, Tabs} from 'antd';
import {FileUploadUtility, WdiFileList} from '@/components/WdiControls/WdiFileUpload';
import DataStore from '@/components/GridWidget/DataStore';
import {DataOptions} from '@/services/basic/metadata';
import {Enum} from '@/enums/system';
import TabPane from '@ant-design/pro-card/lib/components/TabPane';
import {RebatePaymentDetailsForApprovalPanel} from '@/pages/contract/RebatePaymentApply/components/detail';
import DynamicInformation from '@/pages/contract/RebatePaymentApproval/components/DynamicInformation';
import {VatType} from '@/services/mdm/vattype';
import {SoldTo} from '@/services/mdm/soldto';
import {BankAccount} from '@/services/mdm/bankaccount';
import {Company} from '@/services/mdm/company';
import {Supplier} from '@/services/mdm/supplier';
import {FormInvoiceForApprovalPanel} from '@/pages/sales/sp/components/FormInvoice';
import {Invoices} from '@/services/sales/financial/invoices';
import {OaFormApp} from '@/pages/sales/sp/components/OaContractCmpt';
import {OaForm} from '@/services/mdm/oaform';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {SPWarningApprovalApp} from "@/pages/sales/sp/components/WarningInformation";
import {FormSp} from "@/services/sales/sp";

export const RebatePaymentApprovalApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [invoiceDataSource, setInvoiceDataSource] = useState<any[]>([]);
  const [activeKey, setActiveKey] = useState<string>('1');
  const [warningCount, setWarningCount] = useState<number>(0);
  useEffect(() => {
    if (!formRef?.current) {
      return;
    }
    setActiveKey(props.data?.defaultActiveKey ?? '1');
  }, [!!formRef?.current]);

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApprovalPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            amountInVat: viewEntity.formSp.amountInVat,
            remark: viewEntity.formSp.remark,
            fileInfo: FileUploadUtility.parse(
              viewEntity.formSp.fileName,
              viewEntity.formSp.filePath,
            ),
          });
          if (viewEntity.formSp.bankAccountId) {
            BankAccount.get(viewEntity.formSp.bankAccountId).then((res) => {
              formRef.current?.setFieldsValue({
                bankNo: res.data.bankAccountNo,
              });
            });
          }
          if (viewEntity.formSp.companyCode) {
            Company.get(viewEntity.formSp.companyCode).then((res) => {
              formRef.current?.setFieldsValue({
                companyCode: res.data.codeOrName,
              });
            });
          }
          if (viewEntity.formSp.supplierId) {
            Supplier.get(viewEntity.formSp.supplierId).then((res) => {
              formRef.current?.setFieldsValue({
                supplier: res.data.codeOrName,
              });
            });
          }
          if (viewEntity.formSp.bankAccountName) {
            formRef.current?.setFieldsValue({
              bankAccountName: viewEntity.formSp.bankAccountName,
            });
          }
          if (viewEntity.formSp.oaFormNo) {
            OaForm.get(viewEntity.formSp?.oaFormNo).then((res) => {
              formRef.current?.setFieldsValue({
                oaFormNo: res.data,
              });
            });
          }
          FormSp.Buttons.countOfWarning(viewEntity?.formSp?.formSpId || 0).then(response => {
            setWarningCount(response.data);
          });
        }
        if (viewEntity.paymentMethodId) {
          DataOptions.search({typeGroup: 5, inValues: String(viewEntity.paymentMethodId)}).then(
            (responseResult) => {
              formRef.current?.setFieldsValue({
                paymentMethodId: responseResult.data[0].name,
              });
            },
          );
        }
        if (viewEntity.vatTypeId) {
          VatType.get(viewEntity.vatTypeId).then((responseResult) => {
            formRef.current?.setFieldsValue({
              vatTypeId: responseResult.data.vatTypeName,
            });
          });
        }

        if (viewEntity.formSpDetails) {
          setDataSource(DataStore.init(viewEntity.formSpDetails));
          if (viewEntity.formSpDetails[0].voucherNo) {
            formRef.current?.setFieldsValue({
              voucherNo: viewEntity.formSpDetails[0].voucherNo,
            });
          }
          if (viewEntity.pageTypeId == Enum.PageType.PAYMENT_CONTRACT_ACCOUNT_REDUCE) {
            SoldTo.get(String(viewEntity.formSpDetails[0].soldToCode)).then((res) => {
              formRef.current?.setFieldsValue({
                soldToCode: res.data.codeOrName,
              });
            });
          }
          if (viewEntity.paymentMethodId == Enum.PaymentMethod.ACCOUNT_DEDUCT) {
            SoldTo.get(String(viewEntity.formSpDetails[0].soldToCodePayTo)).then((res) => {
              formRef.current?.setFieldsValue({
                soldToCodePayTo: res.data.codeOrName,
              });
            });
          }
        }
        if (
          viewEntity.pageTypeId == Enum.PageType.PAYMENT_CONTRACT_ACCOUNT_REDUCE ||
          viewEntity.pageTypeId == Enum.PageType.PAYMENT_CONTRACT_THIRD_PART
        ) {
          Invoices.getForPayment(viewEntity.formSp?.formSpId || 0).then((responseResult) => {
            setInvoiceDataSource(DataStore.init(responseResult.data));
          });
        }

        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && entity) {
      let title = `返利核销单【${entity.form?.formNo}】`;
      DataOptions.search({typeGroup: 3, value: entity.form?.statusId}).then((response) => {
        if (response.code !== ResponseStatus.SUCCESS) {
          message.error(response.message);
          return;
        }
        if (response.data && response.data.length > 0) {
          if (
            entity.form?.statusId != Enum.FormStatus.SCRAP &&
            entity.form?.exportStatus == Enum.ExportStatus.EXPORT_SUCCESS
          ) {
            title += `，已导出`;
          } else {
            title += `，${response.data[0].name}`;
          }
          if (props.setPageTitle)
            props.setPageTitle(
              <WdiFormPageTitle
                title={title}
                onClose={props.onClose}
                callback={props.callback}
                form={entity?.form}
                useCaseId={Enum.UseCase.RSP}
                moreConfig={moreConfig}
                approvalConfig={approvalConfig}
              />,
            );
        }
      });
    }
  }, [props.setPageTitle, entity]);

  const onDetailsChange = async (data: any[]) => {
    setDataSource(data);
    const refreshEntity = await refreshApprovalPageContext(Number(entity?.form?.formId));
    formRef.current?.setFieldsValue({
      amountInVat: wdiCore.Number.AmountToString(refreshEntity.formSp.amountInVat),
    });
    setInvoiceDataSource(DataStore.init(refreshEntity.invoices));
  };

  return (
    <>
      <WdiStaffCard formId={entity?.form?.formId}/>
      <Tabs activeKey={activeKey} onTabClick={(key, _) => setActiveKey(key)} size="large">
        <TabPane tab={<span>核销信息</span>} key="1">
          <ProForm
            formRef={formRef}
            submitter={{
              render: () => {
                return <></>;
              },
            }}
          >
            <Row gutter={[16, 0]}>
              <Col className="gutter-row" span={4}>
                <Form.Item name="amountInVat" label="本次核销含税总金额">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item name="vatTypeId" label="可抵扣税率类型(普票为0)">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item name="paymentMethodId" label="支付方式">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                span={4}
                hidden={
                  entity?.paymentMethodId != Enum.PaymentMethod.TRANSFER &&
                  entity?.paymentMethodId != Enum.PaymentMethod.THIRD_PART
                }
              >
                <Form.Item name="bankNo" label="银行账号">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                span={4}
                hidden={
                  entity?.paymentMethodId != Enum.PaymentMethod.TRANSFER &&
                  entity?.paymentMethodId != Enum.PaymentMethod.THIRD_PART
                }
              >
                <Form.Item name="bankAccountName" label="户名">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                span={4}
                hidden={entity?.pageTypeId != Enum.PageType.PAYMENT_CONTRACT_ACCOUNT_REDUCE}
              >
                <Form.Item name="soldToCode" label="客户">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                span={4}
                hidden={entity?.paymentMethodId != Enum.PaymentMethod.ACCOUNT_DEDUCT}
              >
                <Form.Item name="soldToCodePayTo" label="付款客户">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                span={4}
                hidden={entity?.pageTypeId != Enum.PageType.PAYMENT_CONTRACT_THIRD_PART}
              >
                <Form.Item name="companyCode" label="公司">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                span={4}
                hidden={entity?.pageTypeId != Enum.PageType.PAYMENT_CONTRACT_THIRD_PART}
              >
                <Form.Item name="supplier" label="供应商">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                span={4}
                hidden={entity?.pageTypeId != Enum.PageType.PAYMENT_CONTRACT_THIRD_PART}
              >
                <Form.Item name="oaFormNo" label="OA合同号">
                  <OaFormApp/>
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item name="voucherNo" label="外部系统单号">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 24]}>
              <Col className="gutter-row" span={16}>
                <Form.Item name="remark" label="核销备注">
                  <WdiEditor readOnly={true}/>
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={8}>
                <Form.Item
                  name="fileInfo"
                  label="核销附件"
                  valuePropName="fileList"
                  getValueFromEvent={(e: any) => {
                    return e.fileList || [];
                  }}
                >
                  <WdiFileList/>
                </Form.Item>
              </Col>
            </Row>
          </ProForm>
          {entity?.form && dataSource && (
            <RebatePaymentDetailsForApprovalPanel
              dataSource={dataSource}
              data={{
                form: entity?.form,
                formSp: entity?.formSp,
                paymentMethod: entity?.paymentMethod,
              }}
              onChange={onDetailsChange}
              onClose={props.onClose}
            />
          )}
          {(entity?.pageTypeId == Enum.PageType.PAYMENT_CONTRACT_ACCOUNT_REDUCE ||
            entity?.pageTypeId == Enum.PageType.PAYMENT_CONTRACT_THIRD_PART) && (
            <FormInvoiceForApprovalPanel dataSource={invoiceDataSource}/>
          )}
        </TabPane>
        <TabPane
          tab={
            <>
              <span>警示信息</span>
              <Badge
                count={warningCount}
                style={{
                  color: 'black',
                  boxShadow: 'none',
                  background: 'none',
                  top: '-20px',
                  right: '-15px',
                  position: 'absolute',
                }}
              ></Badge>
            </>
          }
          key="2"
        >
          <SPWarningApprovalApp formId={entity?.form?.formId}/>
        </TabPane>
        <TabPane tab={<span>动态信息</span>} key="3">
          {entity?.form ? (
            <DynamicInformation
              formId={entity?.form?.formId}
              formTypeId={entity?.form?.formTypeId}
              pageTypeId={entity?.form?.pageTypeId}
              useCaseId={Enum.UseCase.RSP}
            />
          ) : (
            <Spin/>
          )}
        </TabPane>
      </Tabs>
    </>
  );
};
